import React, { useEffect, useMemo, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Layout } from '../../../shared/components'
import { colors, spacing, typography } from '../../../styles/design-system'
import AccountSidebar, { AccountTab } from '../components/AccountSidebar'
import AccountProfileTab, { ProfileFormValues } from '../components/AccountProfileTab'
import AccountAddressesTab from '../components/AccountAddressesTab'
import AccountSecurityTab from '../components/AccountSecurityTab'
import AddressModalForm, { AddressFormValues } from '../components/AddressModalForm'
import { useAuth } from '../../auth/contexts/AuthContext'
import { useToast } from '../../../contexts/ToastContext'
import { useCart } from '../../cart/contexts/CartContext'
import { userService } from '../../../services'
import { Address } from '../../../types/api'
import { useIsMobile } from '../../../hooks/useMediaQuery'

const formatPhone = (value: string) =>
  value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{4})\d+?$/, '$1')

const sanitizeBirthDate = (birthDate?: string | null) => {
  if (!birthDate) return ''
  return birthDate.split('T')[0]
}

const INITIAL_ADDRESS_FORM: AddressFormValues = {
  name: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  zipCode: '',
  isDefault: false
}

const getErrorMessage = (error: unknown) => {
  if (!error) return 'Ocorreu um erro inesperado.'
  if (typeof error === 'string') return error
  if (error instanceof Error) return error.message
  if (typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: string }).message
    if (typeof message === 'string') return message
  }
  return 'Ocorreu um erro inesperado.'
}

const Account: React.FC = () => {
  const navigate = useNavigate()
  const { user, updateUser, isLoading: isAuthLoading } = useAuth()
  const { showError, showSuccess } = useToast()
  const { openCart } = useCart()
  const isMobile = useIsMobile()

  const [activeTab, setActiveTab] = useState<AccountTab>('profile')
  const [profileValues, setProfileValues] = useState<ProfileFormValues>({
    name: '',
    email: '',
    phone: '',
    cpf: '',
    birthDate: ''
  })

  const [addresses, setAddresses] = useState<Address[]>([])
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false)
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false)
  const [addressForm, setAddressForm] = useState<AddressFormValues>(INITIAL_ADDRESS_FORM)
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null)
  const [isSubmittingAddress, setIsSubmittingAddress] = useState(false)
  const [deletingAddressId, setDeletingAddressId] = useState<string | undefined>()
  const [settingDefaultAddressId, setSettingDefaultAddressId] = useState<string | undefined>()
  const [updatingAddressId, setUpdatingAddressId] = useState<string | undefined>()

  useEffect(() => {
    if (user) {
      setProfileValues({
        name: user.name ?? '',
        email: user.email ?? '',
        phone: user.phone ? formatPhone(user.phone) : '',
        cpf: user.cpf ?? '',
        birthDate: sanitizeBirthDate(user.birthDate as string | undefined)
      })
      if (user.addresses && user.addresses.length > 0) {
        setAddresses(user.addresses)
      }
    }
  }, [user])

  const userId = user?.id
  const hasLoadedAddresses = useRef(false)
  const previousUserIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (isAuthLoading || !userId) {
      return
    }

    // Verificar se userId mudou
    const userIdChanged = previousUserIdRef.current !== userId

    // Evitar execuções duplicadas (especialmente no React Strict Mode)
    if (!userIdChanged && hasLoadedAddresses.current) {
      return
    }

    const loadAddresses = async () => {
      hasLoadedAddresses.current = true
      previousUserIdRef.current = userId

      try {
        setIsLoadingAddresses(true)
        const response = await userService.getAddresses()
        const fetchedAddresses = response.addresses ?? []
        setAddresses(fetchedAddresses)
        updateUser({ addresses: fetchedAddresses })
      } catch (error) {
        showError('Não foi possível carregar os endereços', getErrorMessage(error))
      } finally {
        setIsLoadingAddresses(false)
      }
    }

    void loadAddresses()
  }, [isAuthLoading, userId, showError, updateUser])

  const handleProfileChange = (field: keyof ProfileFormValues, value: string) => {
    setProfileValues((previous) => ({
      ...previous,
      [field]: field === 'phone' ? formatPhone(value) : value
    }))
  }

  const handleSaveProfile = async () => {
    if (!user) {
      showError('Sessão expirada', 'Faça login novamente para atualizar seus dados.')
      return
    }

    try {
      setIsSavingProfile(true)
      const payload = {
        name: profileValues.name.trim() || undefined,
        email: profileValues.email.trim() || undefined,
        phone: profileValues.phone.trim() || undefined,
        birthDate: profileValues.birthDate || undefined
      }

      const response = await userService.updateProfile(payload)
      updateUser(response.user)
      showSuccess('Dados atualizados', 'Suas informações foram salvas com sucesso.')
    } catch (error) {
      showError('Não foi possível salvar os dados', getErrorMessage(error))
    } finally {
      setIsSavingProfile(false)
    }
  }

  const openNewAddressModal = () => {
    setEditingAddressId(null)
    setAddressForm(INITIAL_ADDRESS_FORM)
    setIsAddressModalOpen(true)
  }

  const openEditAddressModal = (address: Address) => {
    if (!address.id) {
      showError('Endereço indisponível', 'Não foi possível identificar o endereço selecionado. Tente novamente.')
      return
    }

    setEditingAddressId(address.id)
    setAddressForm({
      id: address.id,
      name: address.name ?? '',
      street: address.street ?? '',
      number: address.number ?? '',
      complement: address.complement ?? '',
      neighborhood: address.neighborhood ?? '',
      city: address.city ?? '',
      state: address.state ?? '',
      zipCode: address.zipCode ?? '',
      isDefault: Boolean(address.isDefault)
    })
    setIsAddressModalOpen(true)
  }

  const closeAddressModal = () => {
    setIsAddressModalOpen(false)
    setAddressForm(INITIAL_ADDRESS_FORM)
    setEditingAddressId(null)
  }

  const handleAddressSubmit = async (data: AddressFormValues) => {
    try {
      setIsSubmittingAddress(true)
      const payload = {
        name: data.name.trim(),
        street: data.street.trim(),
        number: data.number.trim(),
        complement: data.complement?.trim() || undefined,
        neighborhood: data.neighborhood.trim(),
        city: data.city.trim(),
        state: data.state.trim(),
        zipCode: data.zipCode.trim(),
        isDefault: data.isDefault
      }

      if (editingAddressId) {
        setUpdatingAddressId(editingAddressId)
        const response = await userService.updateAddress(editingAddressId, payload)
        const updatedAddresses = addresses.map((current) =>
          current.id === editingAddressId ? response.address : { ...current, isDefault: payload.isDefault ? false : current.isDefault }
        )

        setAddresses(updatedAddresses)
        updateUser({ addresses: updatedAddresses })
        showSuccess('Endereço atualizado', 'Os dados do endereço foram atualizados com sucesso.')
      } else {
        const response = await userService.addAddress(payload)
        let updatedAddresses = [...addresses, response.address]

        if (response.address.isDefault) {
          updatedAddresses = updatedAddresses.map((current) => ({
            ...current,
            isDefault: current.id === response.address.id
          }))
        }

        setAddresses(updatedAddresses)
        updateUser({ addresses: updatedAddresses })
        showSuccess('Endereço adicionado', 'O endereço foi cadastrado com sucesso.')
      }

      closeAddressModal()
    } catch (error) {
      showError('Não foi possível salvar o endereço', getErrorMessage(error))
    } finally {
      setIsSubmittingAddress(false)
      setUpdatingAddressId(undefined)
    }
  }

  const handleDeleteAddress = async (addressId: string) => {
    try {
      setDeletingAddressId(addressId)
      await userService.deleteAddress(addressId)
      const updatedAddresses = addresses.filter((address) => address.id !== addressId)
      setAddresses(updatedAddresses)
      updateUser({ addresses: updatedAddresses })
      showSuccess('Endereço removido', 'O endereço foi excluído com sucesso.')
    } catch (error) {
      showError('Não foi possível remover o endereço', getErrorMessage(error))
    } finally {
      setDeletingAddressId(undefined)
    }
  }

  const handleSetDefaultAddress = async (addressId: string) => {
    try {
      setSettingDefaultAddressId(addressId)
      await userService.updateAddress(addressId, { isDefault: true })
      const updatedAddresses = addresses.map((address) => ({
        ...address,
        isDefault: address.id === addressId
      }))
      setAddresses(updatedAddresses)
      updateUser({ addresses: updatedAddresses })
      showSuccess('Endereço padrão atualizado', 'Endereço padrão definido com sucesso.')
    } catch (error) {
      showError('Não foi possível definir o endereço padrão', getErrorMessage(error))
    } finally {
      setSettingDefaultAddressId(undefined)
    }
  }

  const headerContent = useMemo(
    () => (
      <div style={{ marginBottom: isMobile ? spacing.xl : spacing['2xl'] }}>
        <h1
          style={{
            fontSize: isMobile ? typography.fontSize['2xl'] : typography.fontSize['4xl'],
            fontWeight: typography.fontWeight.bold,
            color: colors.neutral.gray900,
            margin: 0,
            marginBottom: spacing.xs
          }}
        >
          Minha Conta
        </h1>
        <p
          style={{
            color: colors.neutral.gray600,
            margin: 0,
            fontSize: isMobile ? typography.fontSize.sm : typography.fontSize.base
          }}
        >
          Gerencie suas informações pessoais e preferências
        </p>
      </div>
    ),
    [isMobile]
  )

  return (
    <Layout>
      <div
        style={{
          backgroundColor: colors.neutral.gray50,
          minHeight: '100vh',
          paddingTop: isMobile ? spacing.lg : spacing.xl,
          paddingBottom: isMobile ? spacing.lg : spacing.xl
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: `0 ${isMobile ? spacing.md : spacing.lg}` }}>
          {headerContent}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '280px 1fr',
              gap: isMobile ? spacing.lg : spacing.xl
            }}
          >
            <AccountSidebar
              activeTab={activeTab}
              onTabChange={setActiveTab}
              onNavigateToOrders={() => navigate('/pedidos')}
              onNavigateToCart={openCart}
              isMobile={isMobile}
            />

            <div>
              {activeTab === 'profile' && (
                <AccountProfileTab
                  values={profileValues}
                  onChange={handleProfileChange}
                  onSubmit={handleSaveProfile}
                  isSaving={isSavingProfile}
                  isEditable={!isAuthLoading && Boolean(user)}
                  isMobile={isMobile}
                />
              )}

              {activeTab === 'addresses' && (
                <AccountAddressesTab
                  addresses={addresses}
                  onNewAddress={openNewAddressModal}
                  onEditAddress={openEditAddressModal}
                  onDeleteAddress={handleDeleteAddress}
                  onSetDefaultAddress={handleSetDefaultAddress}
                  isLoading={isLoadingAddresses}
                  deletingAddressId={deletingAddressId}
                  updatingAddressId={updatingAddressId}
                  settingDefaultAddressId={settingDefaultAddressId}
                  isMobile={isMobile}
                />
              )}

              {activeTab === 'security' && <AccountSecurityTab isMobile={isMobile} />}
            </div>
          </div>
        </div>
      </div>

      <AddressModalForm
        isOpen={isAddressModalOpen}
        title={editingAddressId ? 'Editar Endereço' : 'Novo Endereço'}
        values={addressForm}
        onClose={closeAddressModal}
        onSubmit={handleAddressSubmit}
        isSubmitting={isSubmittingAddress}
      />
    </Layout>
  )
}

export default Account

